import React, { useMemo, useState } from "react";
import "./styles.scss";
import {
  useAccountSettings,
  printBasicDate,
  URLS,
  useDebounced,
  useVideoScriptSearch,
  exportDataToCsv,
  useSocialCoachSession,
} from "@/utils";
import { Loader, Pagination, Table } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import { Button, Combobox, Input, InputGroup } from "@/components/ui";
import { Search, EyeOff } from "lucide-react";
import { useNewCategories } from "@/utils/useNewCategories";
import { FilterType, WebComponents, WebSettings } from "@/interfaces";
import { VideoScriptsServices } from "@/services/bedrock";
import { CategoryExclusionModal } from "../CategoryExclusionModal";

export const VideoScriptsTable: React.FC = () => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounced(searchTerm, 500);
  const [page, setPage] = useState<number>(1);
  const [selectedCategories, setSelectedCategories] = useState<FilterType[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [isExclusionModalOpen, setIsExclusionModalOpen] = useState<boolean>(false);

  const { isAdmin, isEditor, coachId } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings(coachId);

  const urls = isAdmin ? URLS : isEditor ? URLS.editor : URLS.coach;
  const { categoryOptions } = useNewCategories("VIDEO_SCRIPT", isAdmin ? true : undefined);

  const { scripts, totalPages, isFetching, clearCache } = useVideoScriptSearch({
    page,
    perPage: 40,
    searchTerm: debouncedSearchTerm,
    categoryIds: selectedCategories.map(c => c.value),
  });

  const updateSearch = (s: string) => {
    setPage(1);
    setSearchTerm(s);
  };

  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = isAdmin || hasWritePermission(WebComponents.MANAGE_SCRIPT_LIBRARY);
  const showExclusionsButtons = useMemo(() => {
    if (isEditor) return false;
    if (!hasWritePermission) return false;
    if (isAdmin) return false;
    return !hasAccountSetting(WebSettings.EXCLUDE_GLOBAL_SCRIPTS);
  }, [isEditor, isAdmin, hasAccountSetting, hasWritePermission]);

  const handleExclusionsSaved = async () => {
    setIsExclusionModalOpen(false);
    await clearCache();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const perPage = 50;
      let exportPage = 1;
      const categoryIds = selectedCategories.map(c => c.value);
      const allScripts: any[] = [];
      let stillHasMore = true;
      while (stillHasMore) {
        const response = await VideoScriptsServices.search({
          searchTerm: debouncedSearchTerm,
          categoryIds,
          page: exportPage,
          perPage,
        });
        if (response.content) {
          allScripts.push(...response.content);
        }
        stillHasMore = response.pagination?.totalPages > exportPage;
        exportPage++;
      }

      if (allScripts.length === 0) {
        console.warn("No scripts available to export.");
        return;
      }

      const formattedData = allScripts.map(script => ({
        ID: script.id || "",
        Title: script.title || "",
        Categories: script.categories?.map((c: any) => c.name).join(", ") || "",
        Script: script.content || "",
        "Created Date": script.createdDate ? printBasicDate(script.createdDate) : "",
        "Updated Date": script.updatedDate ? printBasicDate(script.updatedDate) : "",
      }));

      exportDataToCsv("SocialCoachScriptLibrary", formattedData);
    } catch (error) {
      console.error("Failed to export video scripts:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={"videoScriptsTableContainer"}>
      <div className="flex items-center gap-4 flex-wrap justify-between">
        <div className="flex items-center gap-4 flex-wrap flex-auto">
          <InputGroup
            className="max-w-80 w-auto min-w-50"
            control={controlProps => (
              <Input {...controlProps} placeholder="Search scripts..." onChange={e => updateSearch(e.target.value)} />
            )}
            append={<Search />}
          />
          <Combobox
            items={categoryOptions}
            value={selectedCategories}
            itemToStringLabel={category => category.text}
            onValueChange={value => setSelectedCategories(value)}
            placeholder="Filter by Categories"
            emptyText="No Categories found."
            showClear
            searchable
            className="max-w-80 w-auto min-w-50"
            multiple
            maxChips="auto"
          />
          {isFetching && <Loader active inline size="tiny" />}
        </div>

        <div className="flex items-center gap-2">
          {showExclusionsButtons && (
            <Button
              variant="ghost"
              onClick={() => setIsExclusionModalOpen(true)}
              className="flex items-center gap-2 text-gray-500 hover:text-gray-800 px-2"
              title="Manage Category Exclusions"
            >
              <EyeOff size={18} />
              <span className="text-sm">Manage Exclusions</span>
            </Button>
          )}
          {!isEditor && (
            <Button onClick={handleExport} disabled={isExporting} variant="outline">
              {isExporting ? <Loader active inline size="mini" className="mr-2" /> : null}
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          )}
          {withWritePermission && <Button onClick={() => history.push(urls.newVideoScript)} children="New Script" />}
        </div>
      </div>

      <Table striped compact={"very"}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Title</Table.HeaderCell>
            <Table.HeaderCell>Categories</Table.HeaderCell>
            <Table.HeaderCell>Script</Table.HeaderCell>
            <Table.HeaderCell>Created</Table.HeaderCell>
            <Table.HeaderCell>Updated</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {scripts?.content?.map(script => {
            const scriptLink = urls.videoScript.replace(":id", script.id.toString());
            return (
              <Table.Row key={`scriptRow-${script.id}`}>
                <Table.Cell>
                  <a className={"scriptTitle"} onClick={() => history.push(scriptLink)}>
                    {script.title}
                  </a>
                </Table.Cell>

                <Table.Cell>
                  <div className={"categoriesContainer"}>
                    {script.categories?.map(category => {
                      return (
                        <div key={`category-${category.id}`} className={"categoryTag"}>
                          {category.name}
                        </div>
                      );
                    })}
                  </div>
                </Table.Cell>

                <Table.Cell>
                  <a className={"scriptContent"} onClick={() => history.push(scriptLink)}>
                    {script.content}
                  </a>
                </Table.Cell>

                <Table.Cell>
                  <div className={"dateValue"}>{printBasicDate(script.createdDate!)}</div>
                </Table.Cell>
                <Table.Cell>
                  <div className={"dateValue"}>{printBasicDate(script.updatedDate!)}</div>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <div className={"paginationContainer"}>
        <Pagination
          activePage={page}
          onPageChange={(_, { activePage }) => setPage(activePage as number)}
          totalPages={totalPages}
          disabled={isFetching}
          pointing
          secondary
        />
      </div>

      <CategoryExclusionModal
        isOpen={isExclusionModalOpen}
        onClose={() => setIsExclusionModalOpen(false)}
        onSaved={handleExclusionsSaved}
      />
    </div>
  );
};
